import { useState, useEffect } from 'react'

/**
 * Converts snake_case strings to camelCase
 * @param {string} str - Snake case string
 * @returns {string} Camel case string
 */
const snakeToCamel = (str) => {
  return str.replace(/(_\w)/g, (match) => match[1].toUpperCase())
}

/**
 * Recursively converts object keys from snake_case to camelCase
 * @param {*} obj - Object, array, or primitive value
 * @returns {*} Converted object with camelCase keys
 */
const convertKeysToCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToCamelCase(item))
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = snakeToCamel(key)
      acc[camelKey] = convertKeysToCamelCase(obj[key])
      return acc
    }, {})
  }
  return obj
}

/**
 * Custom hook for fetching Peterson's school data from Firebase SQL database
 * 
 * @param {string} pid - Peterson's school ID
 * @param {Object} firebase - Firebase instance with getPetersonsSchoolData method
 * @returns {{ schoolData: Object|null, majorsData: Array, programData: Array, loading: boolean, error: Error|null }}
 */
export const useSQLPetersonsSchool = (pid, firebase) => {
  const [schoolData, setSchoolData] = useState(null)
  const [majorsData, setMajorsData] = useState([])
  const [programData, setProgramData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!pid || !firebase) {
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        setLoading(true)
        const res = await firebase.getPetersonsSchoolData(pid)
        const { schoolData: rawSchoolData, majorsData: rawMajorsData, programData: rawProgramData } = res.data

        setSchoolData(convertKeysToCamelCase(rawSchoolData) || null)
        setMajorsData(rawMajorsData || [])
        setProgramData(rawProgramData || [])
      } catch (err) {
        console.error('Error fetching Peterson\'s school data:', err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [pid, firebase])

  return { schoolData, majorsData, programData, loading, error }
}
